import { defineComponent as _defineComponent } from 'vue'
import { nextTick, reactive, watch } from 'vue';
import CInput from '@/components/shared/CInput';
import CButton from '@/components/shared/CButton';
import { getSetSize, getSharedRepeat } from '@/utils/patternEffects';
import type { PatternElement, SharedRepeatChange } from '@/typings/customilyElements/patternElements';

type Field = 'spacingX' | 'spacingY' | 'offsetX' | 'offsetY';

// Strings: CInput shows a 0 as empty, which would read as Mixed

export default /*#__PURE__*/_defineComponent({
  __name: 'SharedPatternProperties',
  props: {
    origins: null
  },
  emits: ["pattern-change", "refresh-fill", "remove-pattern"],
  setup(__props: any, { emit }: { emit: ({
    (e: 'pattern-change', change: SharedRepeatChange): void;
    (e: 'refresh-fill'): void;
    (e: 'remove-pattern'): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as {
    // null too while the selection isn't all pattern origins: `| null` compiles to a runtime type Vue 2.7 rejects
    origins?: PatternElement[];
};





const model = reactive<Record<Field, string>>({ spacingX: '', spacingY: '', offsetX: '', offsetY: '' });
// What the patterns have: the model lags behind while a "-" is being typed
const applied: Record<Field, string> = { ...model };
// The selection taken as one element: same % as a single pattern, measured on the set
const setSize = reactive({ width: 0, height: 0 });

// `|| 0` turns a -0 into 0
const toPercent = (value: number | null, size: number) =>
    value === null || !size ? '' : String(Math.round((value / size) * 100) || 0);

const initModel = () => {
    const origins = props.origins || [];
    Object.assign(setSize, origins.length ? getSetSize(origins) : { width: 0, height: 0 });
    const shared = getSharedRepeat(origins);
    model.spacingX = toPercent(shared.repeatX === null ? null : shared.repeatX - setSize.width, setSize.width);
    model.spacingY = toPercent(shared.repeatY === null ? null : shared.repeatY - setSize.height, setSize.height);
    model.offsetX = toPercent(shared.shiftX, setSize.width);
    model.offsetY = toPercent(shared.shiftY, setSize.height);
    Object.assign(applied, model);
};

watch(() => props.origins, initModel, { immediate: true });

// A cleared number CInput emits 0, as in PatternProperties: it applies 0% to every pattern
const onInput = (field: Field, value: unknown) => {
    const percent = Number(value);
    if (Number.isNaN(percent)) return;
    // Measured now: the transform panel scales the same selection, so origins (what the watch sees) stay the same
    if (props.origins?.length) Object.assign(setSize, getSetSize(props.origins));
    const fraction = percent / 100;
    const changes: Record<Field, () => SharedRepeatChange> = {
        spacingX: () => ({ repeatX: setSize.width * (1 + fraction) }),
        spacingY: () => ({ repeatY: setSize.height * (1 + fraction) }),
        offsetX: () => ({ shiftX: setSize.width * fraction }),
        offsetY: () => ({ shiftY: setSize.height * fraction })
    };
    const change = changes[field]();
    if ((change.repeatX ?? 1) <= 0 || (change.repeatY ?? 1) <= 0) {
        // Copies at no distance or less: shows what's applied again. Next tick, or the input keeps what was typed
        model[field] = String(percent);
        nextTick(() => (model[field] = applied[field]));
        return;
    }
    applied[field] = String(percent);
    // A lone "-" (or a cleared field) also comes as 0: written back, the input would replace that "-" with a "0"
    if (percent !== 0) model[field] = String(percent);
    emit('pattern-change', change);
};

// Left empty or with a lone "-", it shows the 0 it applied instead of reading as Mixed
const onBlur = (field: Field) => {
    if (applied[field] === '') return;
    model[field] = '';
    nextTick(() => (model[field] = applied[field]));
};

return { __sfc: true,props, emit, model, applied, setSize, toPercent, initModel, onInput, onBlur, CInput, CButton }
}

})